import { Alert, Autocomplete, Box, Button, Card, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormLabel, IconButton, MenuItem, Stack, SxProps, Table, TableBody, TableCell, TableHead, TableRow, TextField, Theme, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDailyRecord, EldDriver, EldEventLine, EldInspection, EldOrganizationSettings, ValidationMessage } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import { useParams, useRouter } from "src/routes/hooks";
import { useResponsive } from "src/hooks/use-responsive";
import { addDaysToDate, daylightSavingsChange, formatDateForPreview, formatDatetimeAsTimeForElog, formatDatetimeForPreview, formatTimeForPreview } from "src/utils/date-utils";
import { Elog, GraphLine } from "src/components/elog";
import { useAuthContext } from "src/auth/hooks";
import NoneFoundReport from "src/components/none-found-report";
import { formatAddress } from "src/utils/address-utils";
import Scrollbar from "src/components/scrollbar";
import Iconify from "src/components/iconify";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import useModelSavingData from "src/hooks/use-model-saving-data";
import TruxTextField from "src/components/inputs/trux-text-field";
import TruxSelectField from "src/components/inputs/trux-select-field";
import TruxAccurateTimeField from "src/components/inputs/trux-accurate-time-field";
import { enqueueSnackbar } from "notistack";
import { Violations } from "src/components/violations";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import dayjs from "dayjs";
import { DateTime } from "luxon";

type DriverLogHeaderProps = {
  upMd: boolean,
  dataRows: string[][][][],
  sx?: SxProps<Theme>
}

function DriverLogHeader({ upMd, dataRows, sx }: DriverLogHeaderProps) {

  return <Stack direction="column" spacing={2} pb={2}>
    {dataRows.map((data, r) => <Stack
      key={r}
      spacing={{ xs: 1, md: 2 }}
      direction={{ xs: 'column', md: 'row' }}
      divider={
        upMd && <Divider
          flexItem
          orientation={upMd ? 'vertical' : 'horizontal'}
          sx={{ borderStyle: 'solid' }}
        />
      }
      sx={{ p: 3, paddingTop: r === 0 ? 2 : 0, paddingBottom: 0 }}
    >
      {data.map((line, i) => (<Stack sx={{ width: 1 }} spacing={0.75} key={i}>
        {line.map((l, j) => (<Box flexGrow={1} flexBasis={0} key={j} display="flex" paddingBottom={0.75} borderBottom={line.length - 1 !== j ? "1px solid #EEE" : "none"}>
          <FormLabel sx={{ color: "#333", fontSize: "13px", fontWeight: 600, flex: 2 }}>
            {l[0]}
          </FormLabel>
          <Box sx={{ flex: 3, fontSize: "13px" }}>
            {l[1]}
          </Box>
        </Box>))}
      </Stack>))
      }
    </Stack>)}
  </Stack>
}

type EventLinesProps = {
  eldEventLines: EldEventLine[],
  timezone: string,
  onEditEvent: (event: EldEventLine) => void,
  isParent: boolean,
}

function EventLines({ eldEventLines, timezone, onEditEvent, isParent }: EventLinesProps) {
  const showDaylightBeforeEventId = useMemo(() => {
    for (let i = 0; i < eldEventLines.length; i+=1) {
      const daylightSavingsChangeTime = daylightSavingsChange.get(eldEventLines[i].eventDate);

      if (daylightSavingsChangeTime) {
        const changeTime = DateTime.fromISO(daylightSavingsChangeTime, { zone: timezone });
        if (changeTime <= DateTime.fromISO(eldEventLines[i].eventTime, { zone: "utc" })) {
          return eldEventLines[i].id;
        }
      }
    }
    return null;
  }, [eldEventLines, timezone]);

  return <>
    <Table size="small">
      <TableHead>
        <TableRow className="avoidbreakinside">
          <TableCell>Time{timezone === "America/New_York" ? "(est)" : "(ct)"}</TableCell>
          <TableCell>Event</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Location</TableCell>
          <TableCell>Origin</TableCell>
          <TableCell><Box display="flex" flexDirection="row" alignItems="center">
            <Typography fontWeight="600" fontSize="13px">Odometer</Typography>
            <Tooltip style={{marginLeft: "4px"}} title={<Box>Origin: ELD Device. Value may differ slightly from the odometer reading on the Map.</Box>}>
              <Iconify icon="mingcute:information-line" width="16px" />
            </Tooltip>
          </Box></TableCell>
          <TableCell>Engine hours</TableCell>
          <TableCell>Notes</TableCell>
          <TableCell>Unit</TableCell>
          <TableCell>Id</TableCell>
          {!isParent && <TableCell />}
        </TableRow>
      </TableHead>
      <TableBody>
        {eldEventLines.map((event, index) => <EldDriverLogLine key={index} event={event} timezone={timezone} onEditEvent={onEditEvent} showDaylight={event.id === showDaylightBeforeEventId} isParent={isParent} />)}
      </TableBody>
    </Table>
    {eldEventLines.length === 0 && <NoneFoundReport />}
  </>
}

function EldDriverLogLine({event, timezone, onEditEvent, showDaylight, isParent}: {event: EldEventLine, timezone: string, onEditEvent: (event: EldEventLine) => void, showDaylight: boolean, isParent: boolean}) {
  return <>
    {showDaylight && <TableRow className="avoidbreakinside">
      <TableCell colSpan={11} sx={{textAlign: "center", backgroundColor: "#fffae6", fontWeight: "bold", color: "#663c00"}}>
        Daylight Savings Time Change (Clock set back 1 hour)
      </TableCell>
    </TableRow>}
    <TableRow className="avoidbreakinside" >
      <TableCell>{formatDatetimeForPreview(event.eventTime, timezone)}</TableCell>
      <TableCell>{event.eventType} {event.eventType === "Certification" ? formatDateForPreview(event.certificationForDate!) : ""}</TableCell>
      <TableCell>{event.eventLineStatus}</TableCell>
      <TableCell>{event.location}</TableCell>
      <TableCell>{event.origin}</TableCell>
      <TableCell>{noOdoAndEngHours(event) ? "" : event.odometer.toFixed(0)}</TableCell>
      <TableCell>{noOdoAndEngHours(event) ? "" : event.engineHours}</TableCell>
      <TableCell>{event.notes}</TableCell>
      <TableCell>{event.vehicleUnitNumber}</TableCell>
      <TableCell>{event.numberId}</TableCell>
      {!isParent && <TableCell>
        <Box displayPrint="none">
          {["Driving", "On Duty", "Sleeper", "Off Duty"].includes(event.eventType) && event.origin === "Driver" && <IconButton size="small" onClick={() => onEditEvent(event)}><Iconify icon="mingcute:edit-2-line" /></IconButton>}
        </Box>
      </TableCell>}
    </TableRow>
  </>
}


type CertificationProps = {
  certificationEldEventLine?: EldEventLine,
  timezone: string,
  eldDriver: EldDriver
}

function Certification({ certificationEldEventLine, timezone, eldDriver }: CertificationProps) {
  return <Box p={2} paddingTop={0} className="avoidbreakinside">
    <Box component="h3">Signature</Box>

    <Box>I hereby certify that my data entries and my record of duty status for this 24-hour period are true and correct.</Box>

    {certificationEldEventLine
      ? <Box>
        <Box pt={5}>
          <img src={eldDriver.signatureUrl} style={{ maxWidth: "200px" }} alt="" />
        </Box>
        <Box>
          <Typography fontSize="small" fontStyle="italic">
            {eldDriver.firstName} {eldDriver.lastName} on {formatDatetimeForPreview(certificationEldEventLine?.eventDate, timezone)}
          </Typography>
        </Box>
      </Box>
      : <Typography component="div" color="error">
        Not Signed
      </Typography>
    }
  </Box>
}

function noOdoAndEngHours(line: EldEventLine) {
  return line.eventType === "Certification" || line.eventType === "Login" || line.eventType === "Logout";
}

type EldDriverLogsSingle = {
  eldDriver: EldDriver,
  certificationEldEventLine?: EldEventLine,
  eldOrganizationSetting: EldOrganizationSettings,
  eldEventLines: EldEventLine[],
  lastPreviousEldEventLine?: EldEventLine,
  endOfToday: string,
  eldDailyRecord: EldDailyRecord,
  eldInspections: EldInspection[],
}

type PageModel = {
  eldDriverLogsSingle?: EldDriverLogsSingle,
}

export function isIOS(): boolean {
  return [
      'iPad',
      'iPhone',
      'iPod'
  ].includes(navigator.platform)
  // iOS 13+ detection
  || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
}

export default function Page() {
  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;
  const isParent = session?.organization?.isParent as boolean;

  const { eldDriverId, logDate } = useParams();

  const [ drivers, setDrivers ] = useState<EldDriver[]>([])

  const [ editDayOpen, setEditDayOpen ] = useState(false);

  const eldDailyRecordModel = useModelSavingData<{
    id: string;
    dailyDate: string;
    eldCoDriverId: number;
    eldCoDriverUsername: string;
    eldCoDriverFirstName: string;
    eldCoDriverLastName: string;

    trailers: string[];
    shippingDocs: string[];
  }>(
    endpoints.eldDailyRecord.updateDailyData,
    "",
  );

  const settings = useSettingsContext();
  const router = useRouter();

  const dataMaintenanceer = useCallback(() => ({
    eldDriverLogsSingle: axios.get(
      formatEndpoint(
        endpoints.eldDriverLogsSingle.findAll, { eldDriverId, logDate }
      )
    ).then(response => response.data),
  }), [eldDriverId, logDate]);

  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataMaintenanceer);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  useEffect(() => {
    setDrivers([])
    axios.get(endpoints.eldDrivers.findAll).then(response => {
      const driversLoaded = response.data as EldDriver[];
      driversLoaded.sort((a, b) => {
        const aFull = `${a.firstName} ${a.lastName}`;
        const bFull = `${b.firstName} ${b.lastName}`;
        if (aFull < bFull) {
          return -1;
        }
        if (aFull > bFull) {
          return 1;
        }
        return 0;
      })
      setDrivers(driversLoaded)
    }).catch(() => {
      enqueueSnackbar("Error retrieving driver list", { variant:"error" })
      setDrivers([])
    });
  }, [session?.organization?.id]);


  const upMd = useResponsive('up', 'md');

  const [isOpenSuggestEldEventLine, setIsOpenSuggestEldEventLine] = useState(false);
  const suggestedEldEventLineModel = useModelSavingData<EldEventLine>(endpoints.eldEventLines.eventSuggested);
  const onSuggest = () => {
    suggestedEldEventLineModel.save().then(response => {
      if (response && data) {
        enqueueSnackbar("Successfully saved the Suggestion");

        const { eldDriverLogsSingle } = data;

        if (eldDriverLogsSingle) {
          eldDriverLogsSingle.eldEventLines.push(suggestedEldEventLineModel.model!)
          eldDriverLogsSingle.eldEventLines.sort((a, b) => {
            if (a.eventTime > b.eventTime) {
              return 1;
            } if (a.eventTime < b.eventTime) {
              return -1;
            }
            return 0;

          })

          updateData("eldDriverLogsSingle")({ ...eldDriverLogsSingle });
        }
        setIsOpenSuggestEldEventLine(false);
      }
    })
  };
  const onEditEvent = (event: EldEventLine) => {
    suggestedEldEventLineModel.set({
      ...event,
      id: crypto.randomUUID(),
      changeRequestedByUserId: session?.user?.id!,
      eventLineStatus: "Inactive Change Requested",
      origin: "Other User",
      changedEldEventLineId: event.id,
    })
    setIsOpenSuggestEldEventLine(true)
  }

  if (!eldDriverId || !logDate) {
    router.push("/app/eld/eld-driver-logs/list")
  }

  const memoizedDataRows = useMemo(() => data ? getHeaderDataRows(data) : [[], []], [data]);
  const todayInTz = dayjs().tz(timezone || "America/Chicago").startOf("day").format("YYYY-MM-DD");

  const logEventLines = useMemo(() => {
    const logEventLinesFinal = [] as {
      eventLine: EldEventLine,
      title: string,
      color: string,
      icon: string,
    }[];

    const lines = data?.eldDriverLogsSingle?.eldEventLines;
    if (!lines) {
      return logEventLinesFinal;
    }

    lines.forEach(l => {
      if (l.eventType === "On Duty") {
        const note = l.notes.toLowerCase();

        if (note.includes("fuel")) {
          logEventLinesFinal.push({
            eventLine: l,
            title: "Fuel",
            color: "#257f31",
            icon: "clarity:fuel-line"
          })
        }

        if (note.includes("accident")) {
          logEventLinesFinal.push({
            eventLine: l,
            title: "Accident",
            color: "#FF5630",
            icon: "tabler:car-crash"
          })
        }
      }
    });

    return logEventLinesFinal;
  }, [data?.eldDriverLogsSingle?.eldEventLines])

  const daylightSavingsChangeTime = logDate ? daylightSavingsChange.get(logDate) : undefined;

  const chartMarkerSpacing = daylightSavingsChangeTime ? 100/27 : 100/26;

  const startOfDayISO = useMemo(() => DateTime.fromISO(`${logDate}T00:00:00`, {zone: timezone}).toISO()!, [logDate, timezone])

  const normalizeEventTime = (datetime: string): number => {
    if (daylightSavingsChangeTime) {
      return DateTime.fromISO(datetime).diff(DateTime.fromISO(startOfDayISO), 'hours').toObject().hours!;
    }
    const parts = formatDatetimeAsTimeForElog(datetime, timezone).split(":");
    return Number((Number(parts[0]) + Number(parts[1]) / 60).toFixed(3));
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <Box sx={{ display: "flex" }}>
          {data && data?.eldDriverLogsSingle &&
            <Box width="100%">
              <PageHeader title={`Log ${formatDateForPreview(logDate)} ${data.eldDriverLogsSingle.eldDriver.firstName} ${data.eldDriverLogsSingle.eldDriver.lastName}`} >
                <Stack gap={2} direction="row" justifyContent="center" displayPrint="none">

                  <Button variant="outlined" onClick={() => {
                    axios({url:
                      formatEndpoint(endpoints.eldDriverLogsSingle.downloadPdf, {eldDriverId, logDate}),
                      method: 'GET',responseType: 'blob',
                    }).then(async (response) => {
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const getBase64 = () => new Promise<string>((resolve) => {
                              const reader = new FileReader();
                              reader.onloadend = () => resolve(reader.result as string);
                              reader.readAsDataURL(blob);
                          });

                      const base64String = await getBase64();

                      function base64ToArrayBuffer(_base64Str: string) {
                        const binaryString = window.atob(_base64Str);
                        const binaryLen = binaryString.length;
                        const bytes = new Uint8Array(binaryLen);
                        for (let i = 0; i < binaryLen; i+=1) {
                              const ascii = binaryString.charCodeAt(i);
                              bytes[i] = ascii;
                       }
                       return bytes;
                      }

                      function showDocument(_base64Str: string, _contentType: string) {
                            const byte = base64ToArrayBuffer(_base64Str);
                            const blobInner = new Blob([byte], { type: _contentType });
                            const blobUrl = URL.createObjectURL(blobInner);
                            if (isIOS()) {
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.click();
                            } else {
                              window.open(blobUrl, "_blank");
                            }
                      }
                      console.log(base64String);
                      showDocument(base64String.split("data:application/pdf;base64,").join(""), "application/pdf");
                    }).catch((e) => {
                      console.error(e);
                      enqueueSnackbar("Unexpected exception has occured. Please contact customer support.", {variant: "error"})
                    })
                  }} startIcon={<Iconify icon="basil:cloud-download-outline" />}>Print</Button>
                  <Button variant="outlined" onClick={() => router.push(`/app/eld/eld-driver-logs/single/${eldDriverId}/${addDaysToDate(logDate!, -1)}`)}>&lt;</Button>
                  <Button disabled={addDaysToDate(logDate!, 1) > todayInTz} variant="outlined" onClick={() => router.push(`/app/eld/eld-driver-logs/single/${eldDriverId}/${addDaysToDate(logDate!, 1)}`)}>&gt;</Button>
                </Stack>
              </PageHeader>

              <Card>
                <Stack direction="column">
                  <DriverLogHeader
                    upMd={upMd}
                    dataRows={[memoizedDataRows[0]]}
                  />
                </Stack>
              </Card>

              <Box sx={{ p: 1 }} />

              <Card>
                <Stack direction="column" position="relative">
                  {!isParent && <Box position="absolute" top="5px" right="10px">
                    <IconButton size="small" onClick={() => {
                        if (data?.eldDriverLogsSingle?.eldDailyRecord) {
                          const {eldDailyRecord} = data.eldDriverLogsSingle;
                          eldDailyRecordModel.set({
                            id: eldDailyRecord.id,
                            dailyDate: eldDailyRecord.dailyDate,
                            eldCoDriverId: eldDailyRecord.eldCoDriverId,
                            eldCoDriverUsername: eldDailyRecord.eldCoDriverUsername,
                            eldCoDriverFirstName: eldDailyRecord.eldCoDriverFirstName,
                            eldCoDriverLastName: eldDailyRecord.eldCoDriverLastName,
                            trailers: eldDailyRecord.trailers,
                            shippingDocs: eldDailyRecord.shippingDocs,
                          });
                          setEditDayOpen(true);
                        }
                      } }>
                      <Iconify icon="mingcute:edit-2-line" />
                    </IconButton>
                  </Box>}
                  <DriverLogHeader
                    upMd={upMd}
                    dataRows={[memoizedDataRows[1]]}
                  />
                </Stack>
              </Card>

              <Box sx={{ p: 1 }} />

              <Card>
                <Stack direction="column">

                  <Scrollbar>
                    <Box sx={{ display: "flex", justifyContent: "center", minWidth: "max-content" }}>
                      <Box position="relative">
                        <Elog lines={getElogLines(data, timezone)} eventDate={data.eldDriverLogsSingle?.eldDailyRecord.dailyDate || logDate!} timezone={timezone} />
                        {data.eldDriverLogsSingle.eldInspections
                        .filter(inspection => inspection.toEventDate === (data.eldDriverLogsSingle?.eldDailyRecord.dailyDate || logDate!))
                        .map((eldInspection, i) => <>
                          <Box key={`line_${i}`} position="absolute" top="18px" left={`calc(${chartMarkerSpacing}% + ${normalizeEventTime(eldInspection.inspectionTime)}* ${chartMarkerSpacing}%)`} width="2.5px" height="112px" sx={{background: "repeating-linear-gradient(to bottom,red 0,red 10px,transparent 10px,transparent 13px);"}} />
                          <Tooltip key={`tooltip_${i}`} title={`DOT Inspection ${formatTimeForPreview(eldInspection.inspectionTime, timezone)}`} color="#056ed8">
                            <IconButton
                              onClick={() => {}}
                              sx={{
                                position: "absolute",
                                top: "126px",
                                left: `calc(${chartMarkerSpacing}% + ${normalizeEventTime(eldInspection.inspectionTime)} * ${chartMarkerSpacing}% - 18px)`,
                                color: "red",
                                p: "6px",
                              }}
                            >
                              <Iconify icon="carbon:police" />
                            </IconButton>
                          </Tooltip>
                        </>)}
                        {logEventLines.filter(entry => entry.eventLine.eventDate === logDate).map((logEventLine, i) => <>
                          <Box key={`line_${i}`} position="absolute" top="18px" left={`calc(${chartMarkerSpacing}% + ${normalizeEventTime(logEventLine.eventLine.eventTime)}* ${chartMarkerSpacing}%)`} width="2px" height="112px" sx={{background: `repeating-linear-gradient(to bottom, ${logEventLine.color} 0, ${logEventLine.color} 10px, transparent 10px, transparent 13px);`}} />
                          <Tooltip key={`tooltip_${i}`} title={`${logEventLine.title} ${formatTimeForPreview(logEventLine.eventLine.eventTime, timezone)}`} color="#056ed8">
                            <IconButton
                              onClick={() => {}}
                              sx={{
                                position: "absolute",
                                top: "126px",
                                left: `calc(4.20% + ${normalizeEventTime(logEventLine.eventLine.eventTime)} * ${chartMarkerSpacing}% - 18px)`,
                                color: logEventLine.color,
                                p: "6px",
                              }}
                            >
                              <Iconify icon={logEventLine.icon} />
                            </IconButton>
                          </Tooltip>
                        </>)}
                      </Box>
                    </Box>

                    <Box sx={{ p: 3 }}>
                      <Violations violations={data.eldDriverLogsSingle.eldDailyRecord.violations} />
                    </Box>

                    <EventLines eldEventLines={data.eldDriverLogsSingle.eldEventLines} timezone={timezone} onEditEvent={onEditEvent} isParent={isParent} />


                    <Box sx={{ p: 1 }} />
                  </Scrollbar>

                  <Box sx={{ p: 1 }} />

                  {!isParent && <Box justifyContent="end" display="flex" flexDirection="row" padding={3} paddingTop={0} displayPrint="none">
                    <Button variant="outlined" startIcon={<Iconify icon="mingcute:add-line" />} onClick={() => {
                      let numberId = 0;

                      data.eldDriverLogsSingle?.eldEventLines.forEach(l => { numberId = Math.max(l.numberId, numberId) })

                      numberId += 1;

                      suggestedEldEventLineModel.set({
                        id: crypto.randomUUID(),
                        organizationId: data.eldDriverLogsSingle?.eldDailyRecord?.organizationId!,
                        updatedAt: null,
                        locked: false,
                        eventTime: `${logDate!}T13:00:00Z`,
                        eventDate: logDate!,
                        certificationForDate: null,

                        eventType: 'On Duty',

                        malfunctionCode: "",

                        numberId,
                        engineHours: 0,
                        vin: data.eldDriverLogsSingle?.eldDailyRecord.vehicleVin || "",
                        vehicleUnitNumber: data.eldDriverLogsSingle?.eldDailyRecord.vehicleUnitNumber || "",
                        eldDeviceSerialNumber: "",
                        eldDeviceSn: "",
                        eldDriverId: Number(eldDriverId!),
                        odometer: 0,
                        eventLineStatus: "Inactive Change Requested",

                        origin: "Other User",
                        changeRequestedByUserId: session?.user?.id!,
                        changedEldEventLineId: "",

                        latLoc: "0",
                        longLoc: "0",
                        locationSource: "Manual",
                        location: "",
                        notes: ""
                      });
                      setIsOpenSuggestEldEventLine(true);
                    }}>Suggest Event</Button>
                  </Box>}
                  <Divider flexItem orientation="vertical" sx={{ border: "1px solid", borderColor: 'background.neutral' }} />

                  <Certification certificationEldEventLine={data.eldDriverLogsSingle.certificationEldEventLine} eldDriver={data.eldDriverLogsSingle.eldDriver} timezone={timezone} />

                  <Box sx={{ p: 1 }} />
                </Stack>
              </Card>
            </Box>
          }
        </Box>
      </DisplayPageData>

      <Dialog fullWidth open={editDayOpen}>
        <DialogTitle sx={{pb: 1}}>Edit Day {formatDateForPreview(eldDailyRecordModel.model?.dailyDate)}</DialogTitle>
        <DialogContent sx={{'-webkit-overflow-scrolling': 'initial'}}>
          {eldDailyRecordModel.model && <Box display="flex" flexDirection="column" gap={2}>
            <Alert icon={<Iconify icon="solar:danger-bold" />} severity='info'>
              The following action is not reversible
            </Alert>
            <TruxFormField label="Co-Driver">
              <Autocomplete
                disablePortal
                value={{
                  id: eldDailyRecordModel.model?.eldCoDriverId,
                  label: `${eldDailyRecordModel.model?.eldCoDriverFirstName} ${eldDailyRecordModel.model?.eldCoDriverLastName}`
                }}
                options={drivers.map(driver => ({
                    id: driver.id,
                    label: `${driver.firstName} ${driver.lastName}`
                  }))}
                onAbort={() => {
                  eldDailyRecordModel.update(selectedDailyRecord => {
                    selectedDailyRecord.eldCoDriverId = 0;
                    selectedDailyRecord.eldCoDriverFirstName = "";
                    selectedDailyRecord.eldCoDriverLastName = "";
                    selectedDailyRecord.eldCoDriverUsername = "";
                  });
                }}
                onChange={(event, option) => {
                  let found = false;
                  drivers.forEach(d => {
                    if (d.id === option?.id) {
                      found = true;
                      eldDailyRecordModel.update(selectedDailyRecord => {
                        selectedDailyRecord.eldCoDriverId = d.id;
                        selectedDailyRecord.eldCoDriverFirstName = d.firstName;
                        selectedDailyRecord.eldCoDriverLastName = d.lastName;
                        selectedDailyRecord.eldCoDriverUsername = d.username;
                      });
                    }
                  });
                  if (!found) {
                    eldDailyRecordModel.update(selectedDailyRecord => {
                      selectedDailyRecord.eldCoDriverId = 0;
                      selectedDailyRecord.eldCoDriverFirstName = "";
                      selectedDailyRecord.eldCoDriverLastName = "";
                      selectedDailyRecord.eldCoDriverUsername = "";
                    });
                  }
                }}
                getOptionKey={(option) => option.id}
                isOptionEqualToValue={(a,b,) => a.id === b.id}
                renderInput={(params) => <TextField {...params} size='small' sx={{width: "100%"}} />}
                sx={{width: "100%"}}
              />
            </TruxFormField>
            <Box>
              <TruxFormField label="Trailers">
                <TruxSimpleTextField value={eldDailyRecordModel.model?.trailers.join(" ") || ""} onChange={value => {
                  eldDailyRecordModel.update(model => {
                    model.trailers = value.split(" ");
                  });
                }} />
              </TruxFormField>
              <Typography component="div" fontSize="12px" textAlign="right" fontStyle="italic">
                Separate values by space
              </Typography>
            </Box>
            <Box>
              <TruxFormField label="Shipping Docs">
                <TruxSimpleTextField value={eldDailyRecordModel.model?.shippingDocs.join(" ") || ""} onChange={value => {
                  eldDailyRecordModel.update(model => {
                    model.shippingDocs = value.split(" ");
                  });
                }} />
              </TruxFormField>
              <Typography component="div" fontSize="12px" textAlign="right" fontStyle="italic">
                Separate values by space
              </Typography>
            </Box>
          </Box>}
        </DialogContent>
        <DialogActions>
          <Button disabled={eldDailyRecordModel.loading} onClick={() => setEditDayOpen(false)}>Cancel</Button>
          <Button disabled={eldDailyRecordModel.loading} type="button" variant='contained' color='success' onClick={() => {
            if (!eldDailyRecordModel.model) {
              return;
            }
            eldDailyRecordModel.update(model => {
              model.trailers = model.trailers.filter(t => t.trim() !== "");
              model.shippingDocs = model.shippingDocs.filter(t => t.trim() !== "");
            })
            eldDailyRecordModel.save().then(() => {
              if (data?.eldDriverLogsSingle && eldDailyRecordModel.model) {
                updateData("eldDriverLogsSingle")({...data.eldDriverLogsSingle, eldDailyRecord: {
                  ...data.eldDriverLogsSingle.eldDailyRecord,
                  ...eldDailyRecordModel.model
                }});
              }

              enqueueSnackbar("Successfully updated the Daily Record");
              setEditDayOpen(false);
            }).catch(e => {
              if (e.response?.status === 422) {
                e.response?.data?.forEach((err: ValidationMessage) => {
                  enqueueSnackbar(`Error: ${  err.message}`, {variant: "error"})
                })
              }
            })
          }} startIcon={eldDailyRecordModel.loading && <CircularProgress size="16px" />}>Save</Button>
        </DialogActions>
      </Dialog>

      {suggestedEldEventLineModel.model &&
        <SimpleDialog
          title="Add Event Suggestion"
          open={isOpenSuggestEldEventLine}
          onCancel={() => setIsOpenSuggestEldEventLine(false)}
          onSubmit={onSuggest}
          submitDisabled={suggestedEldEventLineModel.loading}
          submitButton="Add Suggested Event"
          submitButtonIcon={<Iconify icon="mingcute:add-line" />}
          submitButtonVariant="outlined"
          submitButtonColor="inherit">

          <DisplayErrors savingData={suggestedEldEventLineModel} />

          <Stack direction="column" gap={1}>

            <TruxFormField label="Time">
              <TruxAccurateTimeField modelSavingData={suggestedEldEventLineModel} field="eventTime" timezone={data?.eldDriverLogsSingle?.eldOrganizationSetting.companyTimeZone!} />
            </TruxFormField>

            <TruxFormField label="Type">
              <TruxSelectField modelSavingData={suggestedEldEventLineModel} field="eventType">
                <MenuItem value="Driving">Driving</MenuItem>
                <MenuItem value="On Duty">On Duty</MenuItem>
                <MenuItem value="Sleeper">Sleeper</MenuItem>
                <MenuItem value="Off Duty">Off Duty</MenuItem>
              </TruxSelectField>
            </TruxFormField>


            <TruxFormField label="Location">
              <TruxTextField modelSavingData={suggestedEldEventLineModel} field="location" />
            </TruxFormField>


            <TruxFormField label="Notes">
              <TruxTextField modelSavingData={suggestedEldEventLineModel} field="notes" />
            </TruxFormField>
          </Stack>
        </SimpleDialog>
      }
    </Container>
  );
}

function getLineTypeForElog(eldEventLine: EldEventLine) {
  if (eldEventLine.eventType === "Driving") {
    return "D";
  } if (eldEventLine.eventType === "On Duty") {
    return "ON";
  } if (eldEventLine.eventType === "Sleeper") {
    return "SB";
  } if (eldEventLine.eventType === "Off Duty") {
    return "OFF";
  } if (eldEventLine.eventType === "Personal Use") {
    return "PU";
  } if (eldEventLine.eventType === "Yard Moves") {
    return "YM";
  }
  return null;
}

function getElogLines(data: PageModel, timezone: string): GraphLine[] {
  const lines: GraphLine[] = [];
  const lastPreviousEldEventLine = data.eldDriverLogsSingle?.lastPreviousEldEventLine;
  const eldEventLines = (data.eldDriverLogsSingle?.eldEventLines || []).filter(l => l.eventLineStatus === "Active");
  const endOfToday = data.eldDriverLogsSingle?.endOfToday || "24:00:00";

  if (lastPreviousEldEventLine) {
    // we can get the starting status here
    const lineType = getLineTypeForElog(lastPreviousEldEventLine);

    if (lineType) {
      lines.push({
        state: lineType,
        start: "00:00:00",
        startFull: DateTime.fromISO(`${data.eldDriverLogsSingle!.eldDailyRecord.dailyDate}T00:00:00`, {zone: timezone}).toISO()!,
      })
    } else {
      lines.push({
        state: "OFF",
        start: "00:00:00",
        startFull: DateTime.fromISO(`${data.eldDriverLogsSingle!.eldDailyRecord.dailyDate}T00:00:00`, {zone: timezone}).toISO()!,
      })
    }
  } else {
    lines.push({
      state: "OFF",
      start: "00:00:00",
      startFull: DateTime.fromISO(`${data.eldDriverLogsSingle!.eldDailyRecord.dailyDate}T00:00:00`, {zone: timezone}).toISO()!,
    })
  }

  for (let i = 0; i < eldEventLines.length; i += 1) {
    const line = eldEventLines[i];
    const lineType = getLineTypeForElog(line);
    if (lineType) {
      lines.push({
        state: lineType,
        start: formatDatetimeAsTimeForElog(line.eventTime, timezone),
        startFull: line.eventTime,
      })
    }
  }

  // we need to record the final event for each day at the 24h mark
  lines.push({
    state: lines[lines.length - 1].state,
    start: endOfToday,
    startFull: DateTime.fromISO(`${data.eldDriverLogsSingle!.eldDailyRecord.dailyDate}T${endOfToday}`, {zone: timezone}).toISO()!,
  })

  return lines;
}

function getHeaderDataRows(data: PageModel) {
  const eldDriver = data.eldDriverLogsSingle?.eldDriver;
  const eldOrganizationSetting = data.eldDriverLogsSingle?.eldOrganizationSetting;
  const eldDailyRecord = data.eldDriverLogsSingle?.eldDailyRecord;
  const shippingDocs = data.eldDriverLogsSingle?.eldDailyRecord.shippingDocs;
  const trailers = data.eldDriverLogsSingle?.eldDailyRecord.trailers;
  let activeDiagnostic: EldEventLine | undefined;
  let activeMalfunction: EldEventLine | undefined;

  const usedClears: EldEventLine[] = [];
  const eldEventLines = data.eldDriverLogsSingle?.eldEventLines || [];
  for (let i = 0; i < eldEventLines.length; i += 1) {
    const eldEventLine = eldEventLines[i];
    if (eldEventLine.eventType === "Diagnostic" || eldEventLine.eventType === "Malfunction") {
      let cleared = false;
      const clearedStatus = `${eldEventLine.eventType} Cleared`;
      for (let j = i + 1; j < eldEventLines.length; j += 1) {
        const inner = eldEventLines[j];
        if (inner.eventType === clearedStatus && !usedClears.includes(inner)) {
          usedClears.push(inner);
          cleared = true;
        }
      }
      if (!cleared) {
        if (eldEventLine.eventType === "Diagnostic") {
          activeDiagnostic = eldEventLine;
        } else {
          activeMalfunction = eldEventLine;
        }
      }
    }
  }

  let minOdometer: number | undefined;
  let maxOdometer: number | undefined;
  let minEngineHours: number | undefined;
  let maxEngineHours: number | undefined;
  let deviceSn = "";

  data.eldDriverLogsSingle?.eldEventLines.forEach(eldEventLine => {
    if (eldEventLine.vehicleUnitNumber !== data.eldDriverLogsSingle?.eldDailyRecord.vehicleUnitNumber) {
      return;
    }

    if (deviceSn === "" || eldEventLine.eldDeviceSn) {
      deviceSn = eldEventLine.eldDeviceSn;
    }
    if (eldEventLine.odometer > 1) {
      if (!minOdometer || minOdometer > eldEventLine.odometer) {
        minOdometer = eldEventLine.odometer;
      }
      if (!maxOdometer || maxOdometer < eldEventLine.odometer) {
        maxOdometer = eldEventLine.odometer;
      }
    }
    if (eldEventLine.engineHours > 1) {
      if (!minEngineHours || minEngineHours > eldEventLine.engineHours) {
        minEngineHours = eldEventLine.engineHours;
      }
      if (!maxEngineHours || maxEngineHours < eldEventLine.engineHours) {
        maxEngineHours = eldEventLine.engineHours;
      }
    }
  })

  let odometer = "None";
  if (minOdometer && maxOdometer) {
    if (minOdometer !== maxOdometer) {
      odometer = `${minOdometer.toFixed(0)} - ${maxOdometer.toFixed(0)} (${(maxOdometer -minOdometer).toFixed(0)}mi)`;
    } else {
      odometer = `${minOdometer.toFixed(0)}`;
    }
  }

  let engineHours = "None";
  if (minEngineHours && maxEngineHours) {
    if (minEngineHours !== maxEngineHours) {
      engineHours = `${minEngineHours} - ${maxEngineHours}`;
    } else {
      engineHours = `${minEngineHours}`;
    }
  }

  return [
    [
      [
        ["DRIVER", `${eldDriver?.firstName} ${eldDriver?.lastName}`],
        ["DRIVER ID", `${eldDriver?.username}`],
        ["DRIVER LICENSE", `${eldDriver?.driverLicenseNumber}`],
        ["DL STATE", `${eldDriver?.driverLicenseState}`]
      ],
      [
        ["EXEMPT STATUS", eldDriver?.exemptDriver ? "YES" : "NO"],
        ["CO-DRIVER", `${eldDailyRecord?.eldCoDriverFirstName} ${eldDailyRecord?.eldCoDriverLastName}`],
        ["CO-DRIVER ID", eldDailyRecord?.eldCoDriverUsername || ""],
        ["EXCEPTIONS", ""],

      ],
      [
        ["VEHICLE", eldDailyRecord?.vehicleUnitNumber || ""],
        ["VIN", eldDailyRecord?.vehicleVin || ""],
        ["ODOMETER", odometer],
        ["ENGINE HOURS", engineHours],
      ]
    ],
    [
      [
        ["TRAILERS", trailers?.join(" ") || "None"],
        ["CARRIER", `${eldOrganizationSetting?.companyName}`],
        ["MAIN OFFICE", `${formatAddress(eldOrganizationSetting?.companyAddress)}`],
        ["HOME TERMINAL", `${formatAddress(eldOrganizationSetting?.companyAddress)}`],
        ["DOT NUMBER", `${eldOrganizationSetting?.dotNumber}`]
      ],
      [
        ["SHIPPING DOCS", shippingDocs?.join(" ") || "None"],
        ["SN", deviceSn],
        ["PROVIDER", "Counting Trucks"],
        ["DIAGNOSTIC INDICATOR", activeDiagnostic ? "Yes" : "No"],
        ["MALFUNCTION INDICATOR", activeMalfunction ? "Yes" : "No"],
      ],
    ]
  ]
}
